import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";
import { useHistory } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import EntityLogs from "../../../../../../../../../../../../../../components/Common/EntityLogs/EntityLogs";
import { adminApi } from "../../../../../../../../../../../../../../services/Api/Admin/adminApi";
import {
	CrashReportApi,
	CrashReportOccurrenceApi,
} from "../../../../../../../../../../../../../../services/Api/Admin/adminTypes";
import CrashReportStatus from "../../../../../../../../components/CrashReportStatus";
import CrashReportMarkAsPermanentlyBrokenModal from "../../../../components/CrashReportMarkAsPermanentlyBrokenModal";
import SubmenuCrashReportNavigation from "../../../../components/SubmenuCrashReportNavigation";
import CrashReportBasicInfoCard from "./components/CrashReportBasicInfoCard";
import CrashReportForm from "./components/CrashReportForm";
import CrashReportOccurrencesCard from "./components/CrashReportOccurrencesCard";

interface MatchParams {
	crashReportId: string;
}

const CrashReportsEditIndexPage = () => {
	const { t } = useTranslation("admin");
	const history = useHistory();
	const location = useLocation();
	const [crashReport, setCrashReport] = useState<CrashReportApi | undefined>();
	const [occurrences, setOccurrences] = useState<CrashReportOccurrenceApi[] | undefined>(undefined);
	const { crashReportId } = useParams<MatchParams>();
	const paramsInclude = "occurrences";
	const { addFlash, addSuccessFlash } = useFlash();
	const [showMarkAsPermanentlyBrokenModal, setShowMarkAsPermanentlyBrokenModal] = useState(false);
	const [showLogs, setShowLogs] = useState(false);
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchCrashReport();
		fetchCrashReportOccurrences();
	}, []);

	const fetchCrashReport = async () => {
		const params: Record<string, any> = {
			include: paramsInclude,
		};
		try {
			const res = await adminApi.getCrashReport(parseInt(crashReportId), params);
			setCrashReport(res);
			handleChangeTabTitle(
				`#${res.id} | ${t("modules.crash_report.header.title")} | ${t("lib:go_component.navigation.admin")}`
			);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const fetchCrashReportOccurrences = async () => {
		try {
			const res = await adminApi.getCrashReportsOccurrences();
			setOccurrences(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!crashReport || !occurrences) {
		return <LoadingContainer />;
	}

	const handleCloseCrashReport = async () => {
		try {
			const res = await adminApi.closeCrashReport(crashReport.id, { include: paramsInclude });
			setCrashReport(res);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleRestoreCrashReport = async () => {
		try {
			const res = await adminApi.restoreCrashReport(crashReport.id, { include: paramsInclude });
			setCrashReport(res);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const handleStartFixingCrashReport = async () => {
		try {
			const res = await adminApi.startFixCrashReport(crashReport.id, { include: paramsInclude });
			setCrashReport(res);
			addSuccessFlash(t("common.flash.completed", { ns: "lib" }));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const renderTitle = () => (
		<>
			{`${t("modules.crash_report.header.title")} #${crashReport.id}`}
			<CrashReportStatus status={crashReport.status} />
		</>
	);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.close", { ns: "lib" }),
			action: handleCloseCrashReport,
			variant: "light",
			hide: crashReport.status === "ARCHIVED",
		},
		{
			title: t("common.action.restore", { ns: "lib" }),
			action: handleRestoreCrashReport,
			variant: "light",
			hide: crashReport.status === "OCCURRING",
		},
		{
			title: t("modules.crash_report.action.start_fix.title"),
			action: handleStartFixingCrashReport,
			variant: "light",
			hide: !(crashReport.status !== "ARCHIVED" && crashReport.status !== "WORK_IN_PROGRESS"),
		},
		{
			title: t("modules.crash_report.action.mark_as_permanently_broken.title"),
			action: () => setShowMarkAsPermanentlyBrokenModal(true),
			variant: "light",
			hide: crashReport.status === "PERMANENTLY_BROKEN",
		},
		{
			title: t("lib:common.word.logs"),
			dropdown: true,
			variant: "light",
			action: () => setShowLogs(true),
		},
	];

	return (
		<div className="content">
			<Header title={renderTitle()} buttons={buttons} />
			<SubmenuCrashReportNavigation crashReportId={crashReportId} />
			<CrashReportForm
				crashReport={crashReport}
				handleSuccessAction={setCrashReport}
				handleRestoreCrashReport={handleRestoreCrashReport}
				handleCloseCrashReport={handleCloseCrashReport}
				handleStartFixingCrashReport={handleStartFixingCrashReport}
				setShowMarkAsPermanentlyBrokenModal={setShowMarkAsPermanentlyBrokenModal}
			/>
			<CrashReportBasicInfoCard crashReport={crashReport} />
			{crashReport.occurrences?.length > 0 && <CrashReportOccurrencesCard crashReport={crashReport} />}
			{occurrences?.length > 10 && (
				<Button
					variant="light"
					className="mb-4"
					onClick={() => history.push(`${location.pathname}/occurrences`)}
				>
					{t("common.action.show_more", { ns: "lib" })}
				</Button>
			)}
			{showMarkAsPermanentlyBrokenModal && (
				<CrashReportMarkAsPermanentlyBrokenModal
					isShown={showMarkAsPermanentlyBrokenModal}
					onHide={() => setShowMarkAsPermanentlyBrokenModal(false)}
					include={paramsInclude}
					setCrashReport={setCrashReport}
					ids={[crashReport.id]}
				/>
			)}
			{showLogs && (
				<EntityLogs onHide={() => setShowLogs(false)} resourceId={crashReport.id} resourceType="CRASH_REPORT" />
			)}
		</div>
	);
};

export default CrashReportsEditIndexPage;
